/**
 * RTWO RPC Platform — Domain Models
 * Phase 11 | Module ID: RTWO-RPC-001
 * Version: 1.0.0-phase11
 *
 * Strongly typed domain models for the RTWO RPC Platform.
 * Defines the enterprise RPC interface layer for endpoint registry,
 * method registry, request/response lifecycle, rate limiting,
 * authentication policies, service bindings, and health sources.
 *
 * Truthful Data Policy:
 * - No fake public RPC endpoints claimed as operational.
 * - rpc.rtwo.network is NOT live.
 * - No fake WebSocket connections.
 * - No fake RPC requests, latency, uptime, API keys, or node bindings.
 * - All runtime values are null until real RPC backend services are connected.
 *
 * Source: RPC Platform (RTWO-RPC-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type EndpointId = string;
export type MethodId = string;
export type RequestId = string;
export type ResponseId = string;
export type RateLimitPolicyId = string;
export type AuthPolicyId = string;
export type BindingId = string;
export type HealthSourceId = string;
export type RPCTimestamp = number | null;

export const RPC_PLATFORM_VERSION = {
  moduleId: 'RTWO-RPC-001',
  moduleName: 'RPC Platform',
  version: '1.0.0-phaseA3',
  phase: 'Phase A.3 — Real RPC Provider Backend',
  status: 'JSON-RPC and WebSocket Providers Operational — Public Endpoints Blocked',
  productionReadiness: 'Operational Protocol Providers — Node Endpoints Pending Configuration',
  policy:
    'Truthful Data Policy Enforced — No Fake Endpoints, Requests, Latency, Uptime, API Keys, or Node Bindings',
} as const;

// ─────────────────────────────────────────────
// ENDPOINT TYPE
// ─────────────────────────────────────────────

export type RTWORPCEndpointType =
  | 'public_json_rpc'
  | 'private_json_rpc'
  | 'admin_json_rpc'
  | 'websocket_rpc'
  | 'internal_engine_rpc'
  | 'enterprise_rpc';

export interface EndpointTypeDescriptor {
  endpointType: RTWORPCEndpointType;
  label: string;
  description: string;
  requiresAuthentication: boolean;
  requiresRateLimit: boolean;
  publiclyAccessible: boolean;
  productionReady: boolean;
}

export const ENDPOINT_TYPES: EndpointTypeDescriptor[] = [
  {
    endpointType: 'public_json_rpc',
    label: 'Public JSON-RPC',
    description:
      'Public-facing JSON-RPC endpoint. Accessible without authentication. Rate limiting required. Not deployed — waiting for RPC nodes.',
    requiresAuthentication: false,
    requiresRateLimit: true,
    publiclyAccessible: true,
    productionReady: false,
  },
  {
    endpointType: 'private_json_rpc',
    label: 'Private JSON-RPC',
    description:
      'Private JSON-RPC endpoint. Requires API key authentication. For enterprise and developer access. Not deployed.',
    requiresAuthentication: true,
    requiresRateLimit: true,
    publiclyAccessible: false,
    productionReady: false,
  },
  {
    endpointType: 'admin_json_rpc',
    label: 'Admin JSON-RPC',
    description:
      'Administrative JSON-RPC endpoint. Requires elevated authentication. For node operators and system administrators. Not deployed.',
    requiresAuthentication: true,
    requiresRateLimit: false,
    publiclyAccessible: false,
    productionReady: false,
  },
  {
    endpointType: 'websocket_rpc',
    label: 'WebSocket RPC',
    description:
      'WebSocket-based RPC for real-time subscriptions. Supports event streaming and live chain data. Not deployed — WebSocket is NOT live.',
    requiresAuthentication: false,
    requiresRateLimit: true,
    publiclyAccessible: true,
    productionReady: false,
  },
  {
    endpointType: 'internal_engine_rpc',
    label: 'Internal Engine RPC',
    description:
      'Internal RPC channel between RTWO engines. Used for Core Engine, Consensus, Ledger, and P2P inter-engine communication. Not deployed.',
    requiresAuthentication: true,
    requiresRateLimit: false,
    publiclyAccessible: false,
    productionReady: false,
  },
  {
    endpointType: 'enterprise_rpc',
    label: 'Enterprise RPC',
    description:
      'Enterprise-grade RPC endpoint with SLA guarantees, dedicated rate limits, and priority routing. Requires R7 Enterprise Services. Not deployed.',
    requiresAuthentication: true,
    requiresRateLimit: true,
    publiclyAccessible: false,
    productionReady: false,
  },
];

export const ENDPOINT_TYPE_LABELS: Record<RTWORPCEndpointType, string> = {
  public_json_rpc: 'Public JSON-RPC',
  private_json_rpc: 'Private JSON-RPC',
  admin_json_rpc: 'Admin JSON-RPC',
  websocket_rpc: 'WebSocket RPC',
  internal_engine_rpc: 'Internal Engine RPC',
  enterprise_rpc: 'Enterprise RPC',
};

// ─────────────────────────────────────────────
// ENDPOINT STATUS
// ─────────────────────────────────────────────

export type RTWORPCEndpointStatus =
  | 'architecture_ready'
  | 'not_deployed'
  | 'not_connected'
  | 'not_configured'
  | 'waiting_for_rpc_backend'
  | 'active'
  | 'degraded'
  | 'offline'
  | 'deprecated';

export const ENDPOINT_STATUS_LABELS: Record<RTWORPCEndpointStatus, string> = {
  architecture_ready: 'Architecture Ready',
  not_deployed: 'Not Deployed',
  not_connected: 'Not Connected',
  not_configured: 'Not Configured',
  waiting_for_rpc_backend: 'Waiting for Real RPC Backend',
  active: 'Active',
  degraded: 'Degraded',
  offline: 'Offline',
  deprecated: 'Deprecated',
};

// ─────────────────────────────────────────────
// METHOD TYPE
// ─────────────────────────────────────────────

export type RTWORPCMethodType = 'query' | 'mutation' | 'subscription' | 'admin' | 'internal';

export type RTWORPCMethodStatus =
  'defined' | 'not_implemented' | 'waiting_for_backend' | 'active' | 'deprecated';

// ─────────────────────────────────────────────
// REQUEST LIFECYCLE
// ─────────────────────────────────────────────

export type RTWORPCRequestStatus =
  | 'received'
  | 'authenticated'
  | 'rate_limited'
  | 'validated'
  | 'routed'
  | 'executed'
  | 'responded'
  | 'failed';

export interface RPCRequestLifecycleStage {
  status: RTWORPCRequestStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealBackend: boolean;
  truthfulNote: string;
}

export const RPC_REQUEST_LIFECYCLE: RPCRequestLifecycleStage[] = [
  {
    status: 'received',
    label: 'Received',
    description: 'RPC request received by the gateway. Request ID assigned. Source identified.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real RPC gateway to be deployed. No fake requests shown.',
  },
  {
    status: 'authenticated',
    label: 'Authenticated',
    description:
      'Request authentication verified. API key or token validated against authentication policy.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real authentication backend. No fake API keys or tokens.',
  },
  {
    status: 'rate_limited',
    label: 'Rate Limited',
    description:
      'Request checked against rate limit policy. Requests per minute and burst limit enforced.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real rate limiter. No fake rate limit counters.',
  },
  {
    status: 'validated',
    label: 'Validated',
    description:
      'Request parameters validated against method schema. Method exists and is accessible.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real method registry and schema validation.',
  },
  {
    status: 'routed',
    label: 'Routed',
    description: 'Request routed to the appropriate engine or service binding by the RPC router.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real RPC router and service bindings to be configured.',
  },
  {
    status: 'executed',
    label: 'Executed',
    description: 'Request executed by the target engine or service. Result produced.',
    terminal: false,
    requiresRealBackend: true,
    truthfulNote: 'Requires real engine execution. No fake execution results.',
  },
  {
    status: 'responded',
    label: 'Responded',
    description: 'Response returned to the requesting client. Request lifecycle complete.',
    terminal: true,
    requiresRealBackend: true,
    truthfulNote: 'Requires real RPC response pipeline. No fake latency or response times.',
  },
  {
    status: 'failed',
    label: 'Failed',
    description:
      'Request failed at any lifecycle stage. Error code and message returned to client.',
    terminal: true,
    requiresRealBackend: true,
    truthfulNote: 'Failure state is terminal. Error details must come from real backend.',
  },
];

export const RPC_REQUEST_STATUS_LABELS: Record<RTWORPCRequestStatus, string> = {
  received: 'Received',
  authenticated: 'Authenticated',
  rate_limited: 'Rate Limited',
  validated: 'Validated',
  routed: 'Routed',
  executed: 'Executed',
  responded: 'Responded',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// RESPONSE STATUS
// ─────────────────────────────────────────────

export type RTWORPCResponseStatus = 'success' | 'error' | 'partial' | 'timeout' | 'not_available';

// ─────────────────────────────────────────────
// AUTH TYPE
// ─────────────────────────────────────────────

export type RTWORPCAuthType = 'none' | 'api_key' | 'bearer_token' | 'mutual_tls' | 'internal_token';

// ─────────────────────────────────────────────
// SERVICE BINDING TYPE
// ─────────────────────────────────────────────

export type RTWORPCServiceType =
  | 'core_engine'
  | 'blockchain_protocol_engine'
  | 'consensus_engine'
  | 'ledger_state_engine'
  | 'storage_abstraction_layer'
  | 'p2p_network_engine'
  | 'node_infrastructure'
  | 'validator_infrastructure'
  | 'wallet_engine'
  | 'cryptography_identity_engine'
  | 'system_registry'
  | 'r7_enterprise_services';

// ─────────────────────────────────────────────
// HEALTH SOURCE TYPE
// ─────────────────────────────────────────────

export type RTWORPCHealthSourceType =
  'not_configured' | 'not_connected' | 'gateway_probe' | 'node_probe' | 'internal_check';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWORPCEndpoint
// ─────────────────────────────────────────────

export interface RTWORPCEndpoint {
  endpointId: EndpointId;
  endpointType: RTWORPCEndpointType;
  /** Null until an internal node endpoint is explicitly configured. */
  url: string | null;
  protocol: 'json-rpc-2.0' | 'websocket' | 'internal';
  status: RTWORPCEndpointStatus;
  version: string;
  createdAt: RPCTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWORPCMethod
// ─────────────────────────────────────────────

export interface RTWORPCMethod {
  methodId: MethodId;
  methodName: string;
  namespace: string;
  methodType: RTWORPCMethodType;
  authenticationRequired: boolean;
  rateLimitRequired: boolean;
  status: RTWORPCMethodStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWORPCRequest
// ─────────────────────────────────────────────

export interface RTWORPCRequest {
  requestId: RequestId;
  endpointId: EndpointId;
  methodName: string;
  /** Null until a real request source is identified. */
  requestSource: string | null;
  requestStatus: RTWORPCRequestStatus;
  createdAt: RPCTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWORPCResponse
// ─────────────────────────────────────────────

export interface RTWORPCResponse {
  responseId: ResponseId;
  requestId: RequestId;
  responseStatus: RTWORPCResponseStatus;
  /** Serialized protocol payload or null when unavailable. */
  resultReference: string | null;
  errorCode: number | null;
  createdAt: RPCTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWORPCRateLimitPolicy
// ─────────────────────────────────────────────

export interface RTWORPCRateLimitPolicy {
  policyId: RateLimitPolicyId;
  endpointId: EndpointId;
  /** requestsPerMinute is always null — Truthful Data Policy: no fake rate limit values */
  requestsPerMinute: null;
  /** burstLimit is always null — Truthful Data Policy: no fake burst limit values */
  burstLimit: null;
  status: 'defined' | 'not_configured' | 'active';
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWORPCAuthenticationPolicy
// ─────────────────────────────────────────────

export interface RTWORPCAuthenticationPolicy {
  policyId: AuthPolicyId;
  endpointId: EndpointId;
  authType: RTWORPCAuthType;
  required: boolean;
  status: 'defined' | 'not_configured' | 'active';
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWORPCServiceBinding
// ─────────────────────────────────────────────

export interface RTWORPCServiceBinding {
  bindingId: BindingId;
  endpointId: EndpointId;
  /** nodeId is always null — Truthful Data Policy: RPC Nodes Not Connected */
  nodeId: null;
  serviceType: RTWORPCServiceType;
  status: 'defined' | 'not_connected' | 'active';
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWORPCHealthSource
// ─────────────────────────────────────────────

export interface RTWORPCHealthSource {
  healthSourceId: HealthSourceId;
  endpointId: EndpointId;
  sourceType: RTWORPCHealthSourceType;
  status: 'not_configured' | 'not_connected' | 'active' | 'degraded';
  /** lastCheckedAt is always null — Truthful Data Policy: no fake health check timestamps */
  lastCheckedAt: null;
}

// ─────────────────────────────────────────────
// RPC ENGINE STATUS
// ─────────────────────────────────────────────

export interface RTWORPCEngineStatus {
  moduleId: 'RTWO-RPC-001';
  moduleName: 'RPC Platform';
  phase: 'Phase 11';
  status: 'Structure Complete';
  productionReadiness: 'Pending RPC Nodes and API Gateway';
  endpointCount: 0;
  methodCount: number;
  activeRequestCount: 0;
  connectedNodeCount: 0;
  gatewayDeployed: false;
  publicRPCLive: false;
  webSocketLive: false;
  apiKeysCreated: 0;
  source: 'RPC Platform — Structure Complete — Waiting for Real RPC Backend';
}

// ─────────────────────────────────────────────
// RPC SERVICE IDS
// ─────────────────────────────────────────────

export const RPC_SERVICE_IDS = {
  ENDPOINT_REGISTRY: 'rpc-endpoint-registry',
  METHOD_REGISTRY: 'rpc-method-registry',
  REQUEST_LIFECYCLE: 'rpc-request-lifecycle',
  RESPONSE_LIFECYCLE: 'rpc-response-lifecycle',
  RPC_ROUTER: 'rpc-router',
  RPC_GATEWAY: 'rpc-gateway',
  AUTH_POLICY: 'rpc-authentication-policy',
  RATE_LIMITER: 'rpc-rate-limiter',
  SERVICE_BINDING: 'rpc-service-binding',
  HEALTH_SOURCE_REGISTRY: 'rpc-health-source-registry',
  EVENT_PUBLISHER: 'rpc-event-publisher',
  READINESS_SERVICE: 'rpc-readiness-service',
  PROVIDER_LIFECYCLE: 'rpc-provider-lifecycle',
  PROVIDER_REGISTRATION: 'rpc-provider-registration',
  CONFIGURATION_BINDING: 'rpc-configuration-binding',
  REPOSITORY_BOOTSTRAP: 'rpc-repository-bootstrap',
} as const;

export type RPCRepositoryAccessStatus = 'not_configured' | 'read_only' | 'read_write';

export interface RTWORPCRepositoryDescriptor {
  repositoryId: string;
  repositoryName: string;
  description: string;
  accessStatus: RPCRepositoryAccessStatus;
  recordCount: number | null;
  lastAccessedAt: RPCTimestamp;
}

export const RPC_REPOSITORY_DEFINITIONS: RTWORPCRepositoryDescriptor[] = [
  {
    repositoryId: 'repo-endpoints',
    repositoryName: 'EndpointRepository',
    description: 'Stores RTWORPCEndpoint records ingested from RPC provider.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-methods',
    repositoryName: 'MethodRepository',
    description: 'Stores RTWORPCMethod registry entries.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-requests',
    repositoryName: 'RequestRepository',
    description: 'Stores RTWORPCRequest lifecycle records.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-responses',
    repositoryName: 'ResponseRepository',
    description: 'Stores RTWORPCResponse records. No fabricated responses.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-auth-policies',
    repositoryName: 'AuthPolicyRepository',
    description: 'Stores RTWORPCAuthenticationPolicy definitions.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-rate-limit-policies',
    repositoryName: 'RateLimitPolicyRepository',
    description: 'Stores RTWORPCRateLimitPolicy definitions.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-service-bindings',
    repositoryName: 'ServiceBindingRepository',
    description: 'Stores RTWORPCServiceBinding records.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-health-sources',
    repositoryName: 'HealthSourceRepository',
    description: 'Stores RTWORPCHealthSource records. No fabricated node health.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-event-history',
    repositoryName: 'RPCEventHistoryRepository',
    description: 'Stores RPC platform event history.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
];

export type RPCConfigurationBindingStatus = 'not_bound' | 'bound' | 'active' | 'invalid';

export interface RTWORPCConfigurationBinding {
  bindingId: string;
  configurationId: string;
  configurationName: string;
  providerType: string;
  scope: string;
  status: RPCConfigurationBindingStatus;
  boundAt: RPCTimestamp;
}

export const RPC_CONFIGURATION_BINDINGS: Omit<
  RTWORPCConfigurationBinding,
  'bindingId' | 'boundAt'
>[] = [
  {
    configurationId: 'cfg-004',
    configurationName: 'RPC Gateway Endpoint',
    providerType: 'not_configured',
    scope: 'rpc-platform',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-001',
    configurationName: 'Network Chain ID',
    providerType: 'not_configured',
    scope: 'global',
    status: 'not_bound',
  },
];

export interface Phase23ChecklistItem {
  id: string;
  label: string;
  status: 'ready' | 'pending' | 'blocked';
  note: string;
}

export const PHASE23_RPC_PROVIDER_CHECKLIST: Phase23ChecklistItem[] = [
  {
    id: 'json-rpc-scaffold',
    label: 'JSON-RPC provider implemented',
    status: 'ready',
    note: 'Operational JSON-RPC 2.0 serialization and routing — no fabricated chain data',
  },
  {
    id: 'websocket-scaffold',
    label: 'WebSocket RPC provider implemented',
    status: 'ready',
    note: 'Operational WebSocket frame serialization — connections unavailable until configured',
  },
  {
    id: 'internal-engine-scaffold',
    label: 'Internal engine RPC provider scaffold implemented',
    status: 'ready',
    note: 'Registered — inter-engine RPC channel not implemented',
  },
  {
    id: 'api-gateway-scaffold',
    label: 'API Gateway provider scaffold implemented',
    status: 'ready',
    note: 'Registered — production gateway not deployed',
  },
  {
    id: 'provider-registration',
    label: 'Provider registration and catalog bootstrap',
    status: 'ready',
    note: 'Four providers registered via catalog on engine initialization',
  },
  {
    id: 'configuration-binding',
    label: 'Configuration binding service',
    status: 'ready',
    note: 'Binds cfg-004 to RPC provider selection',
  },
  {
    id: 'repository-bootstrap',
    label: 'Repository bootstrap service',
    status: 'ready',
    note: 'Bootstraps nine repositories when provider is ready',
  },
  {
    id: 'real-rpc',
    label: 'Real RPC network connections',
    status: 'pending',
    note: 'Protocol providers operational. Outbound node connections require internal endpoint configuration.',
  },
  {
    id: 'provider-factory',
    label: 'RPC provider factory',
    status: 'ready',
    note: 'Factory creates operational JSON-RPC/WebSocket and scaffold providers by type',
  },
  {
    id: 'request-routing',
    label: 'Request routing and response serialization',
    status: 'ready',
    note: 'Routes through active provider with JSON-RPC error mapping — truthful unavailable responses',
  },
];

// ─────────────────────────────────────────────
// RPC EVENTS
// ─────────────────────────────────────────────

export type RPCEventType =
  | 'endpoint_registered'
  | 'method_registered'
  | 'request_received'
  | 'request_completed'
  | 'request_failed'
  | 'gateway_status_changed'
  | 'binding_registered'
  | 'rpc_provider_registered'
  | 'rpc_provider_connected'
  | 'configuration_bound'
  | 'repositories_bootstrapped';

export interface RPCEvent {
  eventId: string;
  eventType: RPCEventType;
  moduleId: 'RTWO-RPC-001';
  payload: Record<string, unknown>;
  timestamp: RPCTimestamp;
}

// ─────────────────────────────────────────────
// DEFINED RPC METHODS (Architecture Only)
// ─────────────────────────────────────────────

export const DEFINED_RPC_METHODS: Omit<RTWORPCMethod, 'methodId'>[] = [
  // Chain methods
  {
    methodName: 'rtwo_getBlockByHash',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getBlockByNumber',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getLatestBlock',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getChainState',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  // Transaction methods
  {
    methodName: 'rtwo_getTransaction',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_sendTransaction',
    namespace: 'rtwo',
    methodType: 'mutation',
    authenticationRequired: true,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getTransactionReceipt',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  // Account methods
  {
    methodName: 'rtwo_getBalance',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getAccountState',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  // Validator methods
  {
    methodName: 'rtwo_getValidatorSet',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_getValidatorInfo',
    namespace: 'rtwo',
    methodType: 'query',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  // Subscription methods (WebSocket)
  {
    methodName: 'rtwo_subscribe',
    namespace: 'rtwo',
    methodType: 'subscription',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  {
    methodName: 'rtwo_unsubscribe',
    namespace: 'rtwo',
    methodType: 'subscription',
    authenticationRequired: false,
    rateLimitRequired: true,
    status: 'not_implemented',
  },
  // Admin methods
  {
    methodName: 'admin_getNodeInfo',
    namespace: 'admin',
    methodType: 'admin',
    authenticationRequired: true,
    rateLimitRequired: false,
    status: 'not_implemented',
  },
  {
    methodName: 'admin_getPeerList',
    namespace: 'admin',
    methodType: 'admin',
    authenticationRequired: true,
    rateLimitRequired: false,
    status: 'not_implemented',
  },
  // Internal engine methods
  {
    methodName: 'internal_getEngineStatus',
    namespace: 'internal',
    methodType: 'internal',
    authenticationRequired: true,
    rateLimitRequired: false,
    status: 'not_implemented',
  },
  {
    methodName: 'internal_triggerSync',
    namespace: 'internal',
    methodType: 'internal',
    authenticationRequired: true,
    rateLimitRequired: false,
    status: 'not_implemented',
  },
];
